/* eslint-disable */
import PKCEHelper from './PKCEHelper';

/**
 * Duende Token Manager
 * Handles token storage, validation, refresh, and cleanup
 */

const TOKEN_KEYS = {
  ACCESS_TOKEN: 'duende_access_token',
  REFRESH_TOKEN: 'duende_refresh_token',
  ID_TOKEN: 'duende_id_token',
  TOKEN_EXPIRY: 'duende_token_expiry',
  USER_INFO: 'duende_user_info',
  AUTH_TYPE: 'auth_type' // 'duende' or 'legacy'
};

/**
 * Store Duende tokens (for monitoring/refresh only)
 * NOTE: We store Duende tokens for monitoring and refresh,
 * but API calls use AGC token from backend sync response
 */
function storeTokens(tokens) {
  try {
    console.log('[DuendeTokenManager] Storing Duende tokens', {
      hasAccessToken: !!tokens.access_token,
      hasRefreshToken: !!tokens.refresh_token,
      hasIdToken: !!tokens.id_token,
      expiresIn: tokens.expires_in
    });
    
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, tokens.access_token);
    localStorage.setItem(TOKEN_KEYS.ID_TOKEN, tokens.id_token);
    
    if (tokens.refresh_token) {
      localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, tokens.refresh_token);
    }
    
    // Calculate expiry time (expires_in is in seconds)
    if (tokens.expires_in) {
      const expiryTime = Date.now() + (tokens.expires_in * 1000);
      localStorage.setItem(TOKEN_KEYS.TOKEN_EXPIRY, expiryTime.toString());
    }
    
    // Mark authentication type as Duende (for tracking/monitoring)
    localStorage.setItem(TOKEN_KEYS.AUTH_TYPE, 'duende');
    
    return true;
  } catch (error) {
    console.error('Error storing Duende tokens:', error);
    return false;
  }
}

/**
 * Get access token
 */
function getAccessToken() {
  return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
}

/**
 * Get refresh token
 */
function getRefreshToken() {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
}

/**
 * Get ID token
 */
function getIdToken() {
  return localStorage.getItem(TOKEN_KEYS.ID_TOKEN);
}

/**
 * Get stored user info
 */
function getUserInfo() {
  const userInfoStr = localStorage.getItem(TOKEN_KEYS.USER_INFO);
  if (!userInfoStr) return null;
  
  try {
    return JSON.parse(userInfoStr);
  } catch (error) {
    console.error('Error parsing user info:', error);
    return null;
  }
}

/**
 * Store user info from Duende
 */
function storeUserInfo(userInfo) {
  try {
    localStorage.setItem(TOKEN_KEYS.USER_INFO, JSON.stringify(userInfo));
    return true;
  } catch (error) {
    console.error('Error storing user info:', error);
    return false;
  }
}

/**
 * Check if token is expired
 */
function isTokenExpired() {
  const expiryTime = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY);
  if (!expiryTime) return true;
  
  const now = Date.now();
  const expiry = parseInt(expiryTime, 10);
  
  // Consider expired if less than 5 minutes remaining
  const bufferTime = 5 * 60 * 1000; // 5 minutes
  return now >= (expiry - bufferTime);
}

/**
 * Check if tokens exist
 */
function hasValidTokens() {
  const accessToken = getAccessToken();
  return accessToken && !isTokenExpired();
}

/**
 * Get authentication type
 */
function getAuthType() {
  return localStorage.getItem(TOKEN_KEYS.AUTH_TYPE) || 'legacy';
}

/**
 * Check if using Duende authentication
 */
function isDuendeAuth() {
  return getAuthType() === 'duende';
}

/**
 * Clear all Duende tokens and data
 */
function clearTokens() {
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.ID_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRY);
  localStorage.removeItem(TOKEN_KEYS.USER_INFO);
  localStorage.removeItem(TOKEN_KEYS.AUTH_TYPE);
  
  // Also clear PKCE data if any
  sessionStorage.removeItem('pkce_verifier');
  sessionStorage.removeItem('pkce_state');
}

/**
 * Get token for API calls
 * Priority: AGC token (from backend sync) > Duende access_token
 * This ensures API calls use AGC's backend token, not Duende's
 */
function getTokenForAPI() {
  // Priority 1: Use AGC token stored in legacy 'token' key
  const agcToken = localStorage.getItem('token');
  if (agcToken) {
    console.log('[DuendeTokenManager] Using AGC token for API');
    return agcToken;
  }
  // Priority 2: Fallback to Duende access_token (if no AGC token)
  if (isDuendeAuth()) {
    console.log('[DuendeTokenManager] Using Duende access_token for API (fallback)');
    return getAccessToken();
  }
  return null;
}

/**
 * Get authorization header value
 */
function getAuthorizationHeader() {
  const token = getTokenForAPI();
  if (!token) return null;
  
  if (isDuendeAuth()) {
    return `Bearer ${token}`;
  }
  return token; // Legacy format
}

/**
 * Get time until token expires (in seconds)
 */
function getTimeUntilExpiry() {
  const expiryTime = localStorage.getItem(TOKEN_KEYS.TOKEN_EXPIRY);
  if (!expiryTime) return 0;
  
  const now = Date.now();
  const expiry = parseInt(expiryTime, 10);
  const remaining = Math.max(0, expiry - now);
  
  return Math.floor(remaining / 1000); // Convert to seconds
}

/**
 * Check if token needs refresh (less than 10 minutes remaining)
 */
function needsRefresh() {
  const timeRemaining = getTimeUntilExpiry();
  return timeRemaining < 600; // 10 minutes
}

const DuendeTokenManager = {
  storeTokens,
  getAccessToken,
  getRefreshToken,
  getIdToken,
  getUserInfo,
  storeUserInfo,
  isTokenExpired,
  hasValidTokens,
  getAuthType,
  isDuendeAuth,
  clearTokens,
  getTokenForAPI,
  getAuthorizationHeader,
  getTimeUntilExpiry,
  needsRefresh,
  TOKEN_KEYS
};

export default DuendeTokenManager;
